import apiClient, { handleApiResponse, handleApiError } from './apiClient';

/**
 * Job Service
 * Handles all job-related API calls with comprehensive error handling and logging
 */
class JobService {
  constructor() {
    this.debugMode = import.meta.env.DEV;
    this.log('JobService initialized', { debugMode: this.debugMode });
  }

  /**
   * Enhanced logging with timestamps and context
   * @param {string} message - Log message
   * @param {object} data - Additional data to log
   * @param {string} level - Log level (info, warn, error)
   */
  log(message, data = {}, level = 'info') {
    const timestamp = new Date().toISOString();
    const logMessage = `[JobService ${timestamp}] ${message}`;
    
    if (this.debugMode) {
      switch (level) {
        case 'error':
          console.error(logMessage, data);
          break;
        case 'warn':
          console.warn(logMessage, data);
          break;
        default:
          console.log(logMessage, data);
      }
    }
  }

  /**
   * Get all jobs with filtering and pagination
   * @param {object} filters - Filter parameters
   * @param {number} page - Page number (default: 1)
   * @param {number} pageSize - Number of items per page (default: 20)
   * @returns {Promise<object>} Jobs list with pagination
   */
  async getJobs(filters = {}, page = 1, pageSize = 20) {
    this.log('Getting jobs list', { filters, page, pageSize });
    
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });

      // Add filters to params
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });

      this.log('Jobs request parameters', { 
        paramsString: params.toString(),
        endpoint: `/jobs?${params.toString()}`
      });

      const response = await apiClient.get(`/jobs?${params.toString()}`);
      
      this.log('Jobs response received', { 
        status: response.status,
        jobsCount: response.data?.data?.jobs?.length || 0,
        total: response.data?.data?.total
      });
      
      const data = handleApiResponse(response);
      
      this.log('Jobs data processed successfully', { 
        jobsReturned: data.jobs?.length || 0,
        totalJobs: data.total,
        currentPage: data.page,
        totalPages: data.total_pages
      });
      
      return data;
    } catch (error) {
      this.log('Get jobs failed', { 
        errorMessage: error.message,
        errorCode: error.response?.status,
        responseData: error.response?.data 
      }, 'error');
      
      handleApiError(error);
    }
  }

  /**
   * Get a specific job by ID
   * @param {string} jobId - Job ID
   * @returns {Promise<object>} Job details
   */
  async getJobById(jobId) {
    this.log('Getting job details', { jobId });
    
    try {
      const response = await apiClient.get(`/jobs/${jobId}`);
      
      this.log('Job details response received', { 
        status: response.status,
        jobId: response.data?.data?.job?.id
      });
      
      const data = handleApiResponse(response);
      
      this.log('Job details processed successfully', { 
        jobTitle: data.job?.title,
        jobStatus: data.job?.status,
        department: data.job?.department
      });
      
      return data;
    } catch (error) {
      this.log('Get job details failed', { 
        jobId,
        errorMessage: error.message,
        errorCode: error.response?.status
      }, 'error');
      
      handleApiError(error);
    }
  }

  /**
   * Create a new job posting
   * @param {object} jobData - Job creation data
   * @returns {Promise<object>} Created job details
   */
  async createJob(jobData) {
    this.log('Creating new job', { 
      title: jobData.title,
      department: jobData.department,
      status: jobData.status
    });
    
    try {
      // Validate required fields
      const requiredFields = ['title', 'department', 'location', 'description'];
      const missingFields = requiredFields.filter(field => !jobData[field]);
      
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const response = await apiClient.post('/jobs', jobData);
      
      this.log('Job creation response received', { 
        status: response.status,
        jobId: response.data?.data?.job?.id
      });
      
      const data = handleApiResponse(response);
      
      this.log('Job created successfully', { 
        jobId: data.job?.id,
        title: data.job?.title,
        status: data.job?.status
      });
      
      return data;
    } catch (error) {
      this.log('Job creation failed', { 
        jobTitle: jobData.title,
        errorMessage: error.message,
        errorCode: error.response?.status
      }, 'error');
      
      handleApiError(error);
    }
  }

  /**
   * Check if a job title already exists
   * @param {string} title - Job title to check
   * @returns {Promise<boolean>} True if title exists
   */
  async checkJobTitleExists(title) {
    this.log('Checking if job title exists', { title });
    
    try {
      const response = await this.getJobs({ search: title }, 1, 5);
      const exists = response.jobs?.some(job => 
        job.title.toLowerCase().trim() === title.toLowerCase().trim()
      ) || false;
      
      this.log('Job title exists check completed', { title, exists });
      return exists;
    } catch (error) {
      this.log('Error checking job title existence', { 
        title,
        errorMessage: error.message,
        errorCode: error.response?.status
      }, 'error');
      return false; // Assume doesn't exist on error
    }
  }

  /**
   * Update an existing job
   * @param {string} jobId - Job ID
   * @param {object} jobData - Updated job data
   * @returns {Promise<object>} Updated job details
   */
  async updateJob(jobId, jobData) {
    this.log('Updating job', { jobId, updates: Object.keys(jobData) });
    
    try {
      const response = await apiClient.put(`/jobs/${jobId}`, jobData);
      
      this.log('Job update response received', { 
        status: response.status,
        jobId: response.data?.data?.job?.id
      });
      
      const data = handleApiResponse(response);
      
      this.log('Job updated successfully', { 
        jobId: data.job?.id,
        title: data.job?.title,
        status: data.job?.status
      });
      
      return data;
    } catch (error) {
      this.log('Job update failed', { 
        jobId,
        errorMessage: error.message,
        errorCode: error.response?.status
      }, 'error');
      
      handleApiError(error);
    }
  }

  /**
   * Delete a job
   * @param {string} jobId - Job ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteJob(jobId) {
    this.log('Deleting job', { jobId });
    
    try {
      const response = await apiClient.delete(`/jobs/${jobId}`);
      
      this.log('Job deletion response received', { 
        status: response.status
      });
      
      this.log('Job deleted successfully', { jobId });
      
      return true;
    } catch (error) {
      this.log('Job deletion failed', { 
        jobId,
        errorMessage: error.message,
        errorCode: error.response?.status
      }, 'error');
      
      handleApiError(error);
    }
  }

  /**
   * Publish a job (make it active)
   * @param {string} jobId - Job ID
   * @returns {Promise<object>} Updated job details
   */
  async publishJob(jobId) {
    this.log('Publishing job', { jobId });
    
    try {
      const response = await apiClient.post(`/jobs/${jobId}/publish`);
      
      this.log('Job publish response received', { 
        status: response.status,
        jobId: response.data?.data?.job?.id
      });
      
      const data = handleApiResponse(response);
      
      this.log('Job published successfully', { 
        jobId: data.job?.id,
        status: data.job?.status,
        publishedAt: data.job?.published_at
      });
      
      return data;
    } catch (error) {
      this.log('Job publish failed', { 
        jobId,
        errorMessage: error.message,
        errorCode: error.response?.status
      }, 'error');
      
      handleApiError(error);
    }
  }

  /**
   * Close a job (stop accepting applications)
   * @param {string} jobId - Job ID
   * @returns {Promise<object>} Updated job details
   */
  async closeJob(jobId) {
    this.log('Closing job', { jobId });
    
    try {
      const response = await apiClient.post(`/jobs/${jobId}/close`);
      
      this.log('Job close response received', { 
        status: response.status,
        jobId: response.data?.data?.job?.id
      });
      
      const data = handleApiResponse(response);
      
      this.log('Job closed successfully', { 
        jobId: data.job?.id,
        status: data.job?.status
      });
      
      return data;
    } catch (error) {
      this.log('Job close failed', { 
        jobId,
        errorMessage: error.message,
        errorCode: error.response?.status
      }, 'error');
      
      handleApiError(error);
    }
  }

  /**
   * Get job statistics
   * @returns {Promise<object>} Job statistics
   */
  async getJobStats() {
    this.log('Getting job statistics');
    
    try {
      const response = await apiClient.get('/jobs/stats');
      
      this.log('Job stats response received', { 
        status: response.status,
        hasData: !!response.data?.data
      });
      
      const data = handleApiResponse(response);
      
      this.log('Job statistics processed successfully', { 
        totalJobs: data.total_jobs,
        activeJobs: data.active_jobs,
        draftJobs: data.draft_jobs
      });
      
      return data;
    } catch (error) {
      this.log('Get job stats failed', { 
        errorMessage: error.message,
        errorCode: error.response?.status
      }, 'error');
      
      handleApiError(error);
    }
  }

  /**
   * Search jobs with advanced filtering
   * @param {string} searchTerm - Search term
   * @param {object} filters - Additional filters
   * @param {number} page - Page number
   * @param {number} pageSize - Items per page
   * @returns {Promise<object>} Search results
   */
  async searchJobs(searchTerm, filters = {}, page = 1, pageSize = 20) {
    this.log('Searching jobs', { 
      searchTerm, 
      filtersCount: Object.keys(filters).length,
      page,
      pageSize
    });
    
    const searchFilters = {
      ...filters,
      search: searchTerm,
    };

    return this.getJobs(searchFilters, page, pageSize);
  }

  /**
   * Get jobs by department
   * @param {string} department - Department name
   * @param {number} page - Page number
   * @param {number} pageSize - Items per page
   * @returns {Promise<object>} Jobs in department
   */
  async getJobsByDepartment(department, page = 1, pageSize = 20) {
    this.log('Getting jobs by department', { department, page, pageSize });
    
    return this.getJobs({ department }, page, pageSize);
  }

  /**
   * Get jobs by status
   * @param {string} status - Job status
   * @param {number} page - Page number
   * @param {number} pageSize - Items per page
   * @returns {Promise<object>} Jobs with status
   */
  async getJobsByStatus(status, page = 1, pageSize = 20) {
    this.log('Getting jobs by status', { status, page, pageSize });
    
    return this.getJobs({ status }, page, pageSize);
  }

  /**
   * Validate job data before submission
   * @param {object} jobData - Job data to validate
   * @returns {object} Validation result
   */
  validateJobData(jobData) {
    const errors = {};
    const warnings = [];

    // Required fields
    const requiredFields = {
      title: 'Job title is required',
      department: 'Department is required',
      location: 'Location is required',
      description: 'Job description is required',
      employment_type: 'Employment type is required',
      experience_level: 'Experience level is required'
    };

    Object.entries(requiredFields).forEach(([field, message]) => {
      if (!jobData[field] || jobData[field].trim() === '') {
        errors[field] = message;
      }
    });

    // Salary range validation
    if (jobData.salary_range_min && jobData.salary_range_max) {
      if (parseInt(jobData.salary_range_min) >= parseInt(jobData.salary_range_max)) {
        errors.salary_range_max = 'Maximum salary must be greater than minimum salary';
      }
    }

    // Employment type validation
    const validEmploymentTypes = ['full-time', 'part-time', 'contract', 'internship'];
    if (jobData.employment_type && !validEmploymentTypes.includes(jobData.employment_type)) {
      errors.employment_type = 'Invalid employment type';
    }

    // Experience level validation
    const validExperienceLevels = ['entry', 'mid', 'senior', 'lead', 'executive'];
    if (jobData.experience_level && !validExperienceLevels.includes(jobData.experience_level)) {
      errors.experience_level = 'Invalid experience level';
    }

    // Priority validation
    const validPriorities = ['low', 'medium', 'high', 'urgent'];
    if (jobData.priority && !validPriorities.includes(jobData.priority)) {
      errors.priority = 'Invalid priority level';
    }

    // Status validation
    const validStatuses = ['draft', 'active', 'paused', 'closed', 'cancelled'];
    if (jobData.status && !validStatuses.includes(jobData.status)) {
      errors.status = 'Invalid job status';
    }

    // Warnings for optional but recommended fields
    if (!jobData.salary_range_min && !jobData.salary_range_max) {
      warnings.push('Salary range not specified - this may affect application quality');
    }

    if (!jobData.skills_required || jobData.skills_required.length === 0) {
      warnings.push('No required skills specified - consider adding key skills for better matches');
    }

    const isValid = Object.keys(errors).length === 0;

    this.log('Job data validation completed', {
      isValid,
      errorsCount: Object.keys(errors).length,
      warningsCount: warnings.length
    });

    return {
      isValid,
      errors,
      warnings
    };
  }
}

// Create and export a singleton instance
const jobService = new JobService();
export default jobService;